import { Spinner } from '@heroui/react';
import { FirebaseError } from 'firebase/app';
import { Mail } from 'lucide-react';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useHeroToast } from '@/hooks';
import { useUserState } from '@/state';
import { LocaleLink } from '@/utils/locale';

import { ReactComponent as Logo } from '../../../assets/logo-hs.svg';
import {
  authAccentText,
  AuthContainer,
  AuthForm,
  FormContainer,
  FormWrapper,
  InputGroup,
  InputIcon,
  InputLabel,
  InputWrapper,
  LogoText,
  LogoWrapper,
  MarketingContent,
  MarketingDescription,
  MarketingSection,
  MarketingTitle,
  SignUpLink,
  StyledInput,
  SubmitButton,
  WelcomeSubtitle,
  WelcomeTitle,
} from '../auth.styled';

interface ForgotPasswordFormProps {
  email: string;
}

export const BackButton = styled(LocaleLink)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#a1a1a1' : '#6b7280')};
  font-size: 14px;
  text-decoration: none;
  margin-bottom: 32px;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => authAccentText(theme.mode)};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const SuccessMessage = styled.div`
  padding: 16px;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#14532d' : '#f0fdf4')};
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#166534' : '#86efac')};
  border-radius: 8px;
  margin-bottom: 24px;

  h3 {
    color: ${({ theme }) => (theme.mode === 'dark' ? '#86efac' : '#16a34a')};
    font-size: 16px;
    font-weight: 300;
    margin: 0 0 8px 0;
  }

  p {
    color: ${({ theme }) => (theme.mode === 'dark' ? '#bbf7d0' : '#15803d')};
    font-size: 14px;
    margin: 0;
    line-height: 1.5;
  }
`;

export const ForgotPassword: FC = () => {
  const { t } = useTranslation();
  const { register, handleSubmit, watch } = useForm<ForgotPasswordFormProps>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { showError, showSuccess } = useHeroToast();
  const { resetPassword } = useUserState();

  const email = watch('email');
  const isFormValid = !!email?.trim();

  const getFirebaseErrorMessage = (error: unknown): string => {
    if (error instanceof FirebaseError) {
      const errorKey = `auth.errors.${error.code}`;
      const translated = t(errorKey);
      if (translated !== errorKey) {
        return translated;
      }
    }
    return t('auth.errors.default');
  };

  const onSubmit = async (formData: ForgotPasswordFormProps): Promise<void> => {
    setIsSubmitting(true);
    try {
      await resetPassword(formData.email);
      setEmailSent(true);
      showSuccess(t('auth.forgotPassword.emailSent'));
    } catch (error) {
      showError(getFirebaseErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthContainer>
      <FormContainer>
        <FormWrapper>

          <LogoWrapper>
            <Logo />
          </LogoWrapper>

          <WelcomeTitle>{t('auth.forgotPassword.title')}</WelcomeTitle>
          <WelcomeSubtitle>
            {t('auth.forgotPassword.subtitle')}
          </WelcomeSubtitle>

          {emailSent ? (
            <SuccessMessage>
              <h3>{t('auth.forgotPassword.checkYourEmail')}</h3>
              <p>{t('auth.forgotPassword.emailSentDescription', { email })}</p>
            </SuccessMessage>
          ) : (
            <AuthForm onSubmit={handleSubmit(onSubmit)}>
              <InputGroup>
                <InputLabel>{t('auth.forgotPassword.emailLabel')}</InputLabel>
                <InputWrapper>
                  <InputIcon>
                    <Mail />
                  </InputIcon>
                  <StyledInput
                    type="email"
                    placeholder={t('auth.forgotPassword.emailPlaceholder')}
                    {...register('email', { required: true })}
                  />
                </InputWrapper>
              </InputGroup>

              <SubmitButton type="submit" disabled={!isFormValid || isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner
                      size="sm"
                      classNames={{
                        circle1: 'border-b-white',
                        circle2: 'border-b-white',
                      }}
                    />
                    <span>{t('auth.forgotPassword.sending')}</span>
                  </>
                ) : (
                  t('auth.forgotPassword.sendButton')
                )}
              </SubmitButton>
            </AuthForm>
          )}

          <SignUpLink>
            {t('auth.forgotPassword.rememberPassword')}
            {' '}
            <LocaleLink to="/login">{t('auth.forgotPassword.signIn')}</LocaleLink>
          </SignUpLink>
        </FormWrapper>
      </FormContainer>

      <MarketingSection>
        <MarketingContent>
          <MarketingTitle>
            {t('auth.forgotPassword.marketingTitle')}
          </MarketingTitle>
          <MarketingDescription>
            {t('auth.forgotPassword.marketingDescription')}
          </MarketingDescription>
        </MarketingContent>
      </MarketingSection>
    </AuthContainer>
  );
};
